import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";

/**
 * Ödeme başarılı sayfası.
 *
 * TARAYICIDAKI YÖNLENDİRMEYE ASLA GÜVENMEZ. `merchant_oid`'yi
 * `GET /api/paytr/payment/{oid}` ile sunucudan sorgular ve yalnızca
 * `status === "paid"` döndüğünde "Aboneliğiniz aktif edildi" mesajını
 * gösterir.  Callback henüz işlenmemişse birkaç saniye poll'lar.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | paid | pending | not_found | failed
  const [payment, setPayment] = useState(null);
  const attemptsRef = useRef(0);

  const oid =
    params.get("oid") ||
    params.get("merchant_oid") ||
    (typeof window !== "undefined"
      ? sessionStorage.getItem("penz_last_paytr_oid")
      : null);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const check = async () => {
      if (!oid) {
        setState("not_found");
        return;
      }
      try {
        const { data } = await api.get(`/paytr/payment/${encodeURIComponent(oid)}`);
        if (cancelled) return;
        setPayment(data);
        if (data.status === "paid") {
          setState("paid");
          try { await checkAuth(); } catch { /* auth refresh sessizce */ } // subscription_status güncellensin
        } else if (data.status === "failed") {
          setState("failed");
        } else {
          // pending — callback henüz gelmemiş olabilir; birkaç kez poll'la
          attemptsRef.current += 1;
          if (attemptsRef.current < 8) {
            timer = setTimeout(check, 2000);
            setState("pending");
          } else {
            setState("pending"); // final state; kullanıcı tekrar deneyebilir
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) setState("not_found");
        else {
          // Ağ hatası — bir kez daha dene
          attemptsRef.current += 1;
          if (attemptsRef.current < 5) {
            timer = setTimeout(check, 2000);
          } else {
            setState("not_found");
          }
        }
      }
    };
    check();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [oid, checkAuth]);

  return (
    <div className="space-y-6" data-testid="payment-success-page">
      <StickyPageHead>
        <PageHeader
          title="Ödeme Sonucu"
          subtitle="PayTR ile başlattığınız ödemenin durumu doğrulanıyor"
        />
      </StickyPageHead>

      <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-xl mx-auto text-center">
        {state === "verifying" && (
          <div data-testid="payment-status-verifying">
            <Loader2 className="w-10 h-10 text-slate-400 animate-spin mx-auto" />
            <div className="font-display text-lg font-semibold text-slate-900 mt-4">
              Ödeme doğrulanıyor…
            </div>
            <p className="text-sm text-slate-600 mt-2">
              PayTR&apos;den gelen bildirimi işliyoruz. Bu birkaç saniye sürebilir.
            </p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-status-paid">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="font-display text-xl font-semibold text-slate-900 mt-4">
              Aboneliğiniz aktif edildi
            </div>
            <p className="text-sm text-slate-600 mt-2">
              {payment?.plan === "yearly"
                ? "Yıllık plan başarıyla etkinleştirildi."
                : "Aylık plan başarıyla etkinleştirildi."}{" "}
              Artık tüm modülleri kullanabilirsiniz.
            </p>
            {payment?.total_amount_tl && (
              <p className="text-xs text-slate-500 mt-1">
                Ödenen tutar: {Number(payment.total_amount_tl).toLocaleString("tr-TR")} TL
              </p>
            )}
            <Button
              onClick={() => navigate("/")}
              className="mt-6 bg-slate-900 hover:bg-slate-800"
              data-testid="return-to-app-button"
            >
              Uygulamaya Dön <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-status-pending">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto" />
            <div className="font-display text-lg font-semibold text-slate-900 mt-4">
              Ödeme henüz doğrulanmadı
            </div>
            <p className="text-sm text-slate-600 mt-2">
              PayTR bildirimini bekliyoruz. Ödemeniz kısa süre içinde onaylanacaktır.
              Bu sayfayı yenileyebilir veya uygulamaya döndükten sonra tekrar kontrol
              edebilirsiniz.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                data-testid="payment-retry-verify-button"
              >
                Yeniden Kontrol Et
              </Button>
              <Button
                onClick={() => navigate("/abonelik")}
                variant="ghost"
                data-testid="payment-back-to-subscription"
              >
                Abonelik Sayfası
              </Button>
            </div>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-status-failed">
            <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-rose-600" />
            </div>
            <div className="font-display text-lg font-semibold text-slate-900 mt-4">
              Ödeme tamamlanamadı
            </div>
            <p className="text-sm text-slate-600 mt-2">
              PayTR ödemeyi reddetti. Aboneliğiniz aktif edilmedi.
            </p>
            <Button
              onClick={() => navigate("/abonelik")}
              className="mt-6 bg-slate-900 hover:bg-slate-800"
              data-testid="payment-try-again-button"
            >
              Yeniden Dene
            </Button>
          </div>
        )}

        {state === "not_found" && (
          <div data-testid="payment-status-not-found">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-slate-600" />
            </div>
            <div className="font-display text-lg font-semibold text-slate-900 mt-4">
              Ödeme kaydı bulunamadı
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Bu ödeme referansı bulunamadı. Ödeme sürecini abonelik ekranından
              yeniden başlatabilirsiniz.
            </p>
            <Button
              onClick={() => navigate("/abonelik")}
              className="mt-6 bg-slate-900 hover:bg-slate-800"
              data-testid="payment-not-found-back-button"
            >
              Abonelik Ekranına Dön
            </Button>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400">
          Sorun mu var? <Link to="/support" className="text-slate-600 underline hover:text-slate-900">Destek ekibimize ulaşın</Link>.
        </div>
      </div>
    </div>
  );
}
